import { getShowNetworkTimezone } from '@utils/general'
import moment, { type Moment } from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'

export type CarrierWindowInputMode = 'simple' | 'custom'

const isSimpleWindow = (dates: Moment[]) => !dates.some((d) => d.minutes()) && dates[0].isSame(dates[1], 'day')

export const REQUIRED_SIMPLE_WINDOW_FIELDS = ['date', 'startTime', 'endTime'] as const

const getISODatesFromSimpleWindow = (
  date: Moment,
  startTime: number,
  endTime: number,
  timeZone: Intl.DateTimeFormatOptions['timeZone'] = undefined
) => {
  const showNetworkTime = getShowNetworkTimezone({ timeZone })
  const start = showNetworkTime ? date?.clone().tz(timeZone) : date?.clone()
  const end = showNetworkTime ? date?.clone().tz(timeZone) : date?.clone()
  start.set({ hour: startTime, minute: 0, second: 0, millisecond: 0 }).toISOString(false)
  end.set({ hour: endTime, minute: 0, second: 0, millisecond: 0 }).toISOString(false)
  return [start, end]
}

const eKey = 'carrier_window_eligible'

export const useCarrierWindowOverride = ({ payload, setPayload, wizardOrder }) => {
  const [inputMode, setInputMode] = useState<CarrierWindowInputMode>('simple')
  const [isOverrideToggled, setIsOverrideToggled] = useState(false)

  useEffect(() => {
    if (!isOverrideToggled) {
      clearWindow()
      removeDatesFromPayload()
    }
  }, [isOverrideToggled])

  // Input state for custom mode
  const [customWindow, setCustomWindow] = useState<Moment[]>([undefined, undefined])

  // Input state for simple mode
  const [simpleWindow, setSimpleWindow] = useState({
    date: undefined,
    startTime: undefined,
    endTime: undefined,
    timezone: undefined,
  })
  const showCarrierWindowOverride = useMemo(
    () => payload?.[eKey] ?? wizardOrder?.[eKey],
    [payload?.[eKey], wizardOrder?.[eKey]]
  )

  const isToggleDisabled = useMemo(
    () => payload?.carrier_window_override || wizardOrder?.carrier_window_override,
    [payload, wizardOrder]
  )

  const initializeWindowState = (orderData, timezone: Intl.DateTimeFormatOptions['timeZone']) => {
    if (orderData.type === 'backhaul') {
      return
    }
    // We need to look at the wizard order, not just the payload - for existing orders

    const existingStart = orderData.carrier_window_start
    const existingEnd = orderData.carrier_window_end
    const existingOverride = orderData?.carrier_window_override

    setPayload({
      ...payload,
      carrier_window_start: existingStart,
      carrier_window_end: existingEnd,
    })

    if (existingStart && existingEnd) {
      initializeInputState([moment(existingStart), moment(existingEnd)], timezone)
    }

    if (existingOverride) {
      setIsOverrideToggled(true)
    }
  }

  const handleSimpleWindowChange = (window) => {
    setSimpleWindow(window)

    // Keeping custom input state in sync with the simple input state changes
    const cloned = window?.date?.clone()
    const [start, end] = getISODatesFromSimpleWindow(cloned, window?.startTime, window?.endTime, window?.timezone)

    // Always save the dates in UTC on the payload, otherwise we work in local time for the user.
    setPayload({
      ...payload,
      carrier_window_start: start,
      carrier_window_end: end,
    })

    setCustomWindow([start, end])
  }

  const handleCustomWindowChange = (
    date,
    dates: [string, string],
    timeZone: Intl.DateTimeFormatOptions['timeZone'] = undefined
  ) => {
    const showNetworkTime = getShowNetworkTimezone({ timeZone })
    // Weird, but the onChange gives us back date strings, we need to convert those back to moments before feeding them back to the date picker as a value.
    const moments = date.map((d, i) => moment(d).year(customWindow[i]?.year() || moment().year()))

    // we can use the date instead of date strings
    setCustomWindow(date)
    // Keeping simple input state in sync with the custom input state
    setSimpleWindow({
      date: moments[0],
      startTime: moments[0].hour(),
      endTime: moments[1].hour(),
      timezone: timeZone,
    })

    const start = showNetworkTime ? moments[0].clone().tz(timeZone) : moments[0].clone()
    const end = showNetworkTime ? moments[1].clone().tz(timeZone) : moments[1].clone()

    start.set({ hour: moments[0].hour(), minute: moments[0].minute(), second: 0, millisecond: 0 }).toISOString(false)
    end.set({ hour: moments[1].hour(), minute: moments[1].minute(), second: 0, millisecond: 0 }).toISOString(false)

    // Always save the dates in UTC on the payload, otherwise we work in local time for the user.
    setPayload((payload) => ({
      ...payload,
      carrier_window_start: start,
      carrier_window_end: end,
    }))
  }

  const clearAndReset = () => {
    setInputMode('simple')
    setIsOverrideToggled(false)
    clearWindow()
  }

  const removeDatesFromPayload = () => {
    setPayload({
      ...payload,
      carrier_window_start: undefined,
      carrier_window_end: undefined,
    })
  }

  const clearWindow = () => {
    setCustomWindow([undefined, undefined])
    setSimpleWindow({
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      timezone: undefined,
    })
  }

  const initializeInputState = (startingDates: Moment[], timezone: Intl.DateTimeFormatOptions['timeZone']) => {
    if (!startingDates) return
    setInputMode(isSimpleWindow(startingDates) ? 'simple' : 'custom')
    const [start, end] = startingDates

    setSimpleWindow({
      date: start,
      startTime: start.hour(),
      endTime: start.date() === end.date() && end.hour(),
      timezone,
    })
    setCustomWindow(startingDates)
  }

  return {
    isToggleDisabled,
    isOverrideToggled,
    setIsOverrideToggled,
    showCarrierWindowOverride,
    inputMode,
    setInputMode,
    simpleWindow,
    setSimpleWindow,
    customWindow,
    setCustomWindow,
    handleCustomWindowChange,
    handleSimpleWindowChange,
    clearWindow,
    removeDatesFromPayload,
    clearAndReset,
    initializeWindowState,
  }
}
